<script setup lang="ts">
/**
 * Stale price mark (Figma `Icon/outdated` `3612:68948`, boards `3609:69145` /
 * `3609:119658`): a thin muted clock with the date in a tooltip.
 *
 * Hover on desktop, tap on mobile. The icon is its own button: inside a
 * product card (a link) a tap opens the tooltip and does not open the product.
 * 16 px glyph, 44 px tap area on touch screens.
 */
import { FairIcon } from '@/ui/primitives/FairIcon'
import { FairTooltip } from '@/ui/primitives/FairTooltip'
import type { FairTooltipPlacement } from '@/ui/tokens/fair-tooltip.data'

withDefaults(defineProps<{
  tooltip: string
  /** Accessible name of the button; the tooltip text is the description. */
  label?: string
  placement?: FairTooltipPlacement
  size?: number
}>(), {
  label: 'Price may be out of date',
  placement: 'top',
  size: 16,
})
</script>

<template>
  <FairTooltip
    class="fair-outdated-mark"
    :content="tooltip"
    :placement="placement"
    trigger="auto"
  >
    <button
      type="button"
      class="fair-outdated-mark__button"
      :aria-label="label"
      :title="tooltip"
      @click.stop.prevent
    >
      <FairIcon
        name="outdated"
        :size="size"
      />
    </button>
  </FairTooltip>
</template>

<style scoped>
.fair-outdated-mark {
  display: inline-flex;
  vertical-align: middle;
}

.fair-outdated-mark__button {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: none;
  color: var(--color-text-muted);
  cursor: help;
}

.fair-outdated-mark__button:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring);
  border-radius: var(--radius-control);
}

/* 44 px tap target without growing the glyph (`3609:119742`) */
@media (pointer: coarse) {
  .fair-outdated-mark__button::before {
    content: '';
    position: absolute;
    inset: 50% auto auto 50%;
    width: var(--touch-min);
    height: var(--touch-min);
    transform: translate(-50%, -50%);
  }
}
</style>
